import { useRef, useState } from "react";
import { UploadCloud, FileText, X, FolderOpen } from "lucide-react";
import { toast } from "sonner";

const ACCEPTED_DOC = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];
const MAX_DOC_MB = 10;

/*
 * Zone de dépôt de document (cahier des charges, fiche produit… PDF / DOCX / TXT).
 * - Desktop : drag & drop + clic.
 * - Mobile : bouton dédié "Parcourir mes documents" (ouvre picker natif — iCloud/Drive/Files).
 */
export default function DocumentDrop({ onFile, file, disabled = false, testid = "document-drop", hint }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const accept = (f) => {
    if (!f) return;
    const okName = /\.(pdf|docx|txt)$/i.test(f.name || "");
    if (!ACCEPTED_DOC.includes(f.type) && !okName) return toast.error("Format non supporté (PDF, DOCX, TXT)");
    if (f.size > MAX_DOC_MB * 1024 * 1024) return toast.error(`Document trop volumineux (max ${MAX_DOC_MB} Mo)`);
    onFile(f);
  };

  const clear = (e) => { e.stopPropagation(); onFile(null); if (inputRef.current) inputRef.current.value = ""; };
  const openBrowse = (e) => { e.stopPropagation(); if (!disabled) inputRef.current?.click(); };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testid}
      onClick={openBrowse}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !disabled) inputRef.current?.click(); }}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) accept(e.dataTransfer.files?.[0]); }}
      className={`relative cursor-pointer rounded-xl border-2 border-dashed transition-colors duration-200 p-5 sm:p-8 text-center
        ${over ? "border-primary bg-accent" : "border-border bg-white hover:border-primary/50"}
        ${disabled ? "opacity-60 pointer-events-none" : ""}`}
    >
      <input ref={inputRef} type="file" accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        className="hidden" data-testid={`${testid}-input`} onChange={(e) => accept(e.target.files?.[0])} />
      {file ? (
        <div className="relative inline-flex items-center gap-3 bg-accent/60 rounded-lg px-4 sm:px-5 py-3 sm:py-4 max-w-full" data-testid={`${testid}-file`}>
          <FileText className="w-7 h-7 sm:w-8 sm:h-8 text-primary shrink-0" strokeWidth={1.6} />
          <div className="text-left min-w-0">
            <p className="font-medium text-sm truncate max-w-[180px] sm:max-w-[220px]">{file.name}</p>
            <p className="text-[11px] font-mono text-muted-foreground">{(file.size / 1024).toFixed(0)} Ko</p>
          </div>
          <button onClick={clear} data-testid={`${testid}-clear`}
            className="ml-2 w-7 h-7 rounded-full bg-white border border-border shadow-sm flex items-center justify-center hover:bg-muted shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 py-2 sm:py-4">
          <div className="w-14 h-14 rounded-full bg-accent text-primary flex items-center justify-center">
            <UploadCloud className="w-6 h-6" strokeWidth={1.8} />
          </div>
          <p className="hidden sm:block font-serif text-lg text-foreground">Glissez-déposez un document</p>
          <p className="sm:hidden font-serif text-base text-foreground">Document PDF / Word</p>
          <p className="text-sm text-muted-foreground max-w-sm">{hint || "Cahier des charges, fiche produit, brochure."}</p>

          {/* Bouton mobile parcourir */}
          <button
            type="button"
            onClick={openBrowse}
            className="sm:hidden inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-white text-sm font-semibold py-2.5 px-5 hover:bg-[hsl(var(--primary-light))] active:scale-95 transition"
            data-testid={`${testid}-browse`}
          >
            <FolderOpen className="w-4 h-4" /> Parcourir mes documents
          </button>

          <p className="hidden sm:block text-xs text-muted-foreground">ou cliquez pour parcourir.</p>

          <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-muted-foreground mt-1">
            <FileText className="w-3.5 h-3.5" /> PDF · DOCX · TXT · max {MAX_DOC_MB} Mo
          </span>
        </div>
      )}
    </div>
  );
}
